import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Phone, MessageSquare, Mail, Check, UserPlus, Clock, TrendingUp, Users } from "lucide-react";
import api from "../../lib/api";
import { PageHeader, Card, Stat, Badge, Empty } from "../../components/crm/ui";
import { toast } from "sonner";

export default function Dashboard() {
  const [d, setD] = useState(null);
  const load = () => api.get("/dashboard").then(({ data }) => setD(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const complete = async (taskId) => {
    try { await api.post(`/tasks/${taskId}/complete`, {}); toast.success("Follow-up completed"); load(); }
    catch { toast.error("Could not complete"); }
  };

  if (!d) return <div className="text-slate-400 text-sm">Loading dashboard…</div>;
  const m = d.metrics;

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Who needs attention, what's overdue, and where contacts are coming from." />

      {/* Needs Attention */}
      <Card className="p-6 mb-6 border-l-4 border-l-alto-red" data-testid="needs-attention-card">
        <div className="flex items-center gap-2 mb-4"><AlertTriangle className="text-alto-red" size={20} /><h2 className="font-display text-lg font-bold text-slate-900">Needs Attention</h2></div>
        {d.needs_attention.length === 0 ? (
          <p className="text-sm text-slate-500">You're all caught up — nothing is falling through the cracks. 🎯</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {d.needs_attention.map((n) => (
              <div key={n.key} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50" data-testid={`needs-${n.key}`}>
                <span className={`w-2 h-2 rounded-full ${n.severity === "high" ? "bg-alto-red" : "bg-amber-500"}`} />
                <span className="text-sm text-slate-700">{n.message}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label="New Contacts" value={m.new_contacts} icon={UserPlus} to="/app/contacts" />
        <Stat label="Need Follow-Up" value={m.need_follow_up} tone="red" icon={Clock} to="/app/follow-ups" />
        <Stat label="Overdue" value={m.overdue} tone="warn" icon={AlertTriangle} to="/app/follow-ups?scope=overdue" />
        <Stat label="Due Today" value={m.due_today} icon={Clock} to="/app/follow-ups" />
        <Stat label="Due This Week" value={m.due_this_week} icon={Clock} />
        <Stat label="Recently Contacted" value={m.recently_contacted} tone="good" icon={Check} />
        <Stat label="Unassigned" value={m.unassigned} tone="red" icon={Users} />
        <Stat label="Converted" value={m.converted} tone="good" icon={TrendingUp} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Today's follow-ups */}
        <Card className="lg:col-span-2 p-6">
          <h2 className="font-display text-lg font-bold text-slate-900 mb-4">Today's Follow-Ups</h2>
          {d.todays_followups.length === 0 ? <Empty icon={Clock} title="No follow-ups due" hint="Create follow-up tasks to keep contacts moving." /> : (
            <div className="divide-y divide-slate-100">
              {d.todays_followups.map((t) => (
                <div key={t.task_id} className="py-3 flex items-center justify-between gap-3" data-testid={`today-task-${t.task_id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Link to={`/app/contacts/${t.contact_id}`} className="font-semibold text-slate-900 text-sm hover:text-alto-red truncate">{t.contact_name || "Unnamed"}</Link>
                      {t.overdue && <Badge tone="red">Overdue</Badge>}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 truncate">{t.reason} · {t.source || "—"} · {t.assigned}</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {t.phone && <a href={`tel:${t.phone}`} className="p-2 rounded-md hover:bg-slate-100 text-slate-500" title="Call"><Phone size={15} /></a>}
                    {t.phone && <a href={`sms:${t.phone}`} className="p-2 rounded-md hover:bg-slate-100 text-slate-500" title="Text"><MessageSquare size={15} /></a>}
                    {t.email && <a href={`mailto:${t.email}`} className="p-2 rounded-md hover:bg-slate-100 text-slate-500" title="Email"><Mail size={15} /></a>}
                    <button onClick={() => complete(t.task_id)} data-testid={`complete-${t.task_id}`} className="p-2 rounded-md hover:bg-emerald-50 text-emerald-600" title="Complete"><Check size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Source activity + recent */}
        <div className="space-y-6">
          <Card className="p-6">
            <h2 className="font-display text-lg font-bold text-slate-900 mb-4">Source Activity</h2>
            {d.source_activity.length === 0 ? <p className="text-sm text-slate-400">No data yet.</p> : (
              <div className="space-y-3">
                {d.source_activity.map((s) => {
                  const max = Math.max(...d.source_activity.map((x) => x.count), 1);
                  return (
                    <div key={s.source}>
                      <div className="flex justify-between text-sm mb-1"><span className="text-slate-600">{s.source}</span><span className="font-semibold text-slate-900">{s.count}</span></div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-alto-red rounded-full" style={{ width: `${(s.count / max) * 100}%` }} /></div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
          <Card className="p-6">
            <h2 className="font-display text-lg font-bold text-slate-900 mb-4">Recent Contacts</h2>
            {d.recent_contacts.length === 0 ? <p className="text-sm text-slate-400">No contacts yet.</p> : (
              <div className="space-y-2.5">
                {d.recent_contacts.map((c) => (
                  <Link key={c.id} to={`/app/contacts/${c.id}`} className="flex items-center justify-between group">
                    <span className="text-sm text-slate-700 group-hover:text-alto-red truncate">{c.first_name} {c.last_name}</span>
                    <Badge>{c.latest_source || "—"}</Badge>
                  </Link>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
